import { cn } from "@/utils";
import type { ReactNode } from "react";

/**
 * Estados que toda tela precisa ter desenhados antes de ser considerada pronta:
 * vazio, carregando e erro de carregamento.
 */

export interface EmptyStateProps {
  title: string;
  /** Diga o que fazer a seguir, não só que está vazio. */
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Vazio é o estado mais comum de uma tela nova, e por isso ele não pode
 * chamar mais atenção que a tela cheia. A caixa tracejada com ícone fazia o
 * contrário: desenhava uma moldura em volta de "não há nada aqui", e o olho
 * ia para o buraco em vez de ir para a lista ao lado ou para o botão que
 * resolve. Ficam o título e o texto que diz o que fazer a seguir.
 */
export const EmptyState = ({
  title,
  description,
  action,
  className,
}: EmptyStateProps) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-2 px-4 py-10 text-center sm:py-14",
      className
    )}
  >
    <h3 className="font-ds-display text-ds-h4 text-ds-ink-900">{title}</h3>
    {description ? (
      <p className="font-ds-sans text-ds-small text-ds-ink-600 max-w-sm">
        {description}
      </p>
    ) : null}
    {action ? <div className="mt-2 w-full sm:w-auto">{action}</div> : null}
  </div>
);

/**
 * Esqueleto de carregamento. Preferimos esqueleto a spinner em tela de conteúdo:
 * ele mantém o layout estável e comunica o que está por vir.
 */
export const Skeleton = ({ className }: { className?: string }) => (
  <span
    aria-hidden="true"
    className={cn(
      "rounded-ds-sm bg-ds-ink-100 block animate-pulse motion-reduce:animate-none",
      className
    )}
  />
);

/** Região que anuncia carregamento a leitores de tela sem poluir a tela. */
export const LoadingRegion = ({
  label = "Carregando conteúdo",
  children,
}: {
  label?: string;
  children: ReactNode;
}) => (
  // `relative` para o rótulo sr-only não se ancorar no documento.
  <div role="status" aria-live="polite" aria-busy="true" className="relative">
    <span className="sr-only">{label}</span>
    {children}
  </div>
);
